import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useSignupModal } from '../context/SignupModalContext';
import PageBuilder from '../components/PageBuilder';

import { BrandLogo } from '../lib/branding';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openSignupModal } = useSignupModal();
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (user) {
      // Nouveau retailer avec profil incomplet → Mon Compte
      if (user.role === 'retailer' && user.account_status === 'a_completer') {
        navigate('/dashboard/profil', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
      return;
    }
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [user, navigate]);

  // Header component for landing page
  const LandingHeader = (
    <header className="sticky top-0 z-50 ludo-glass">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center group">
              <BrandLogo size="md" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')} 
              data-testid="header-login-button" 
              translate="no"
              className="text-sm font-medium"
              style={{ color: 'var(--ludo-text-secondary)' }}
            >
              Connexion
            </Button>
            <Button 
              onClick={openSignupModal} 
              data-testid="header-signup-button" 
              translate="no"
              className="rounded-full px-5 text-sm font-semibold"
            >
              Créer un compte
            </Button>
          </div>
        </div>
      </div>
    </header>
  );

  // Footer component for landing page
  const LandingFooter = (
    <footer className="ludo-footer py-8">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-white/40">
        <p>&copy; 2026 Games Visor. Tous droits réservés.</p>
      </div>
    </footer>
  );

  return (
    <>
      {/* Dynamic Page Builder loads all blocks from backend */}
      <PageBuilder 
        pageId="page-landing"
        isPublic={true}
        customHeader={LandingHeader}
        customFooter={LandingFooter}
        canvasBackground="#F7F5F2"
      />

      {/* Welcome Popup */}
      <Dialog open={showPopup} onOpenChange={setShowPopup}>
        <DialogContent data-testid="welcome-popup" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading tracking-tight" style={{ color: 'var(--ludo-text)' }}>Bienvenue sur Games Visor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p style={{ color: 'var(--ludo-text-secondary)' }}>
              La plateforme dédiée aux professionnels de l'univers du jeu !
            </p>
            <p className="text-sm" style={{ color: 'var(--ludo-text-secondary)' }}>
              Découvrez comment nous vous aidons à ne plus perdre de temps dans vos recherches et à prendre les bonnes décisions pour votre magasin.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button onClick={() => {
                setShowPopup(false);
                openSignupModal();
              }} className="w-full h-11 rounded-lg font-semibold" data-testid="popup-signup-button" translate="no">
                Créer un compte
              </Button>
              <Button variant="outline" onClick={() => {
                setShowPopup(false);
                navigate('/login');
              }} className="w-full h-11 rounded-lg font-medium" style={{ borderColor: 'var(--ludo-border)' }} data-testid="popup-login-button" translate="no">
                Connexion
              </Button>
              <button onClick={() => setShowPopup(false)} data-testid="popup-close-button" className="w-full text-sm py-2 transition-colors" style={{ color: 'var(--ludo-text-secondary)' }}>
                Fermer
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LandingPage;
